// The order form across a sign-in redirect. A submit that meets an expired session, or a token
// renewal that has to leave the page, would otherwise lose everything the user typed. The draft
// lives in sessionStorage (dies with the tab, like the sign-in tokens) and is read back once
// by the order screen, which then clears it.

import type { OrderForm } from '@hearingaid/shared'

/** Namespaced so it can never collide with the sign-in library's own (`oidc.`) session entries. */
const KEY = 'hearingaid.draftOrder'

export function keepDraft(storage: Storage, form: OrderForm): void {
  try {
    storage.setItem(KEY, JSON.stringify(form))
  } catch {
    // Storage full or blocked: the form is still on screen; only the redirect case loses it.
  }
}

export function clearDraft(storage: Storage): void {
  try {
    storage.removeItem(KEY)
  } catch {
    // nothing to do
  }
}

/**
 * The kept draft; null when there is none or it is unreadable. Reading does not forget it (a
 * render may run twice): the screen calls `clearDraft` once it has mounted with it.
 */
export function readDraft(storage: Storage): OrderForm | null {
  let raw: string | null = null
  try {
    raw = storage.getItem(KEY)
  } catch {
    return null
  }
  if (raw === null) return null
  try {
    const form = JSON.parse(raw) as OrderForm
    if (typeof form !== 'object' || form === null || typeof form.submission_key !== 'string') return null
    return form
  } catch {
    return null
  }
}

/** Every key this app keeps in sessionStorage (draft, deep link): on sign-out none may outlive the user. */
export function clearAppSession(storage: Storage): void {
  try {
    const keys: string[] = []
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i)
      if (k !== null && k.startsWith('hearingaid.')) keys.push(k)
    }
    for (const k of keys) storage.removeItem(k)
  } catch {
    // blocked storage holds nothing of ours
  }
}
